/**
 * Privacy & Terms — lightweight, calm legal pages.
 *
 * Designed to satisfy the production-onboarding requirement before live
 * MYOB / Xero financial data flows through the platform. Plain English,
 * matches the executive UX, no marketing fluff.
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "20 May 2026";
const CONTACT_EMAIL = "michael@kindraai.dev";

function Shell({ title, children, testId }) {
    return (
        <div className="min-h-screen bg-[#fafaf9] py-12 px-6" data-testid={testId}>
            <div className="max-w-[760px] mx-auto">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-[13px] text-stone-500 hover:text-stone-800 transition-colors mb-8"
                >
                    <ArrowLeft size={13} strokeWidth={2} />
                    Back to the Command Centre
                </Link>
                <h1 className="text-display-md mb-2">{title}</h1>
                <div className="text-[12.5px] text-stone-500 mb-10">
                    Last updated: {LAST_UPDATED}
                </div>
                <div className="space-y-7 text-[14.5px] leading-[1.7] text-stone-700">
                    {children}
                </div>
            </div>
        </div>
    );
}

function H({ children }) {
    return (
        <h2 className="text-[16px] font-semibold text-stone-900 mt-8 mb-2 first:mt-0">
            {children}
        </h2>
    );
}

export function Privacy() {
    return (
        <Shell title="Privacy Statement" testId="privacy-page">
            <p>
                The All Crane Command Centre is a private operational
                intelligence platform built for All Crane Sales. This page
                explains, in plain English, what data the platform handles and
                how it's protected.
            </p>

            <H>What data the platform holds</H>
            <p>
                Once production accounting integrations are connected, the
                platform will hold financial figures, customer and supplier
                lists, invoice and bill records, and bank-transaction
                summaries pulled from connected MYOB and Xero company files,
                plus connected bank feeds. It will also hold the deterministic
                analyses, reconciliations, audit findings, forecasts, AI
                advisor exchanges, and the user accounts authorised to access
                the platform.
            </p>
            <p>
                The platform does not currently collect personal customer
                details beyond what already exists in the connected
                accounting system. It does not run third-party advertising
                trackers, marketing pixels, or analytics beacons.
            </p>

            <H>Where the data lives</H>
            <p>
                All data is stored on a private server operated by the
                business in Australia/Germany region. The database is bound to
                localhost only — never publicly exposed. All public traffic
                is served over HTTPS with strict transport security enabled.
                Internal backend services are reverse-proxied behind nginx
                and are not directly reachable from the public internet.
            </p>

            <H>Who can see it</H>
            <p>
                Only authenticated users with valid credentials issued to the
                organisation can access the platform. Role-based access
                controls scope what each user can see (owner / admin /
                operator). Sessions use signed tokens; secrets are rotated on
                a regular cadence.
            </p>

            <H>AI advisor data</H>
            <p>
                The platform includes an AI advisor that answers commercial
                questions grounded in the platform's own deterministic truth
                layer. To answer questions, the relevant business context
                (already inside the platform) is sent to a managed large-
                language-model provider. The provider returns a generated
                answer that is stored against the asking user. The platform
                does not send sensitive personal information, raw bank-
                account numbers, or customer contact details to the AI
                provider.
            </p>

            <H>Retention</H>
            <p>
                Synced financial data is retained for as long as the
                organisation requires it for operational and audit purposes.
                The owner can request deletion of any specific record or of
                the entire dataset at any time by contacting the operator.
                Encrypted backups are retained for disaster recovery.
            </p>

            <H>Contact</H>
            <p>
                For any privacy question, deletion request, or concern,
                contact{" "}
                <a
                    className="text-[var(--brand)] underline"
                    href={`mailto:${CONTACT_EMAIL}`}
                >
                    {CONTACT_EMAIL}
                </a>
                .
            </p>
        </Shell>
    );
}

export function Terms() {
    return (
        <Shell title="Terms of Use" testId="terms-page">
            <p>
                These terms apply to use of the All Crane Command Centre — a
                private operational intelligence platform built for All Crane
                Sales. By logging in you accept these terms.
            </p>

            <H>1. Authorised use</H>
            <p>
                Access is restricted to users explicitly authorised by All
                Crane Sales. Credentials must not be shared. Suspected
                compromise of a credential must be reported immediately to
                the operator so the account can be revoked.
            </p>

            <H>2. Demo data</H>
            <p>
                Until live MYOB / Xero / bank-feed connections are completed,
                the platform displays demonstration figures clearly labelled
                "Demo data" throughout the interface. These figures are
                deterministic mock values and must not be used for real
                financial decisions.
            </p>

            <H>3. AI advisor — informational only</H>
            <p>
                The AI advisor produces interpretations of the platform's
                data. It is informational only. It is not financial,
                accounting, tax, or legal advice. Material decisions must be
                confirmed with a qualified accountant or advisor. The
                platform will refuse to answer or downgrade its confidence
                level when underlying data is stale, missing, or based on
                demo data.
            </p>

            <H>4. Data accuracy</H>
            <p>
                The platform synthesises figures from connected systems and
                applies deterministic reconciliation, audit, and forecasting
                rules. Where source data is incorrect, the platform's output
                will reflect that. Users should review confidence indicators
                and evidence drawers before acting on numbers.
            </p>

            <H>5. No write-back</H>
            <p>
                The platform is read-only with respect to connected
                accounting systems. It will never post journals, modify
                invoices, or alter records inside MYOB, Xero, or any other
                connected system.
            </p>

            <H>6. Availability</H>
            <p>
                The platform is provided on a best-effort availability basis
                without a formal SLA. Planned maintenance windows will be
                communicated. The operator may suspend access in the event
                of suspected misuse, security incident, or vendor
                instability.
            </p>

            <H>7. Changes to these terms</H>
            <p>
                These terms may be updated as the platform evolves —
                particularly around new integrations, role changes, and data
                handling. Material changes will be communicated to active
                users.
            </p>

            <H>8. Contact</H>
            <p>
                Operator contact:{" "}
                <a
                    className="text-[var(--brand)] underline"
                    href={`mailto:${CONTACT_EMAIL}`}
                >
                    {CONTACT_EMAIL}
                </a>
                .
            </p>
        </Shell>
    );
}
